import { convertPercentageToDecimal } from "~/utils/general";
import _ from "lodash";
import { useStore } from "vuex";

export class UserContext {
  constructor(app, store, redirect) {
    this.app = app;
    this.store = store;
    this.redirect = redirect;
    this.store = useStore();
    this.auth = useAuth();
  }

  async metaRefresh(refreshParams) {
    refreshParams = {
      clearState: refreshParams?.clearState ?? false,
      stateUpdate: refreshParams?.stateUpdate ?? {},
      refresh: refreshParams?.refresh ?? new Set(),
    };

    const route = useRoute();
    const queryBranchCode = route.query.franchise_id?.toUpperCase();
    const queryCustomerId = route.query.c;
    const queryNdc = route.query.ndc;
    const queryNdcTracking = route.query.ndc_tracking;
    const queryCoupon = route.query.coupon;

    try {
      /* Overwrite existing Franchise ID and Customer Number from URL query params, execute full user context refresh. */
      /* NB: If reloading data fails due to bad queryBranchCode and/or queryCustomerId, the store data will be mostly cleared. */
      if (queryBranchCode && queryCustomerId) {
        const currentBranchCode = this.store.state.persistedState?.branchCode;
        const currentCustomerId = this.store.state.persistedState?.cust_No;

        if (
          !currentBranchCode ||
          !currentCustomerId ||
          queryBranchCode != currentBranchCode ||
          queryCustomerId != currentCustomerId
        ) {
          refreshParams.clearState = true;
          refreshParams.stateUpdate.branchCode = queryBranchCode;
          refreshParams.stateUpdate.custNo = Number(queryCustomerId);
          refreshParams.refresh.add("ALL");
        }
      }

      /* Overwrite existing NDC and refresh NDC */
      if (queryNdc) {
        // Execute NDC lookup and tracking regardless of matching existing NDC info.
        refreshParams.stateUpdate.ndc = {
          ndc: queryNdc,
          tracking: queryNdcTracking ? queryNdcTracking : "ndc",
        };
        refreshParams.refresh.add("SCOPE_INFO");
      }

      /* Overwrite existing coupon */
      if (queryCoupon) {
        refreshParams.stateUpdate.addCouponCode = queryCoupon;

        refreshParams.refresh.add("ORDER_INFO");
      }

      // Merge all route.meta.userContext data
      // route.meta.forEach(meta => {
      //   if (meta.userContext) {
      //     refreshParams.clearState =
      //       refreshParams.clearState || (meta.userContext.clearState ?? false);

      //     refreshParams.stateUpdate = {
      //       ...refreshParams.stateUpdate,
      //       ...meta.userContext.stateUpdate
      //     };

      //     meta.userContext.refresh?.forEach(item => {
      //       refreshParams.refresh.push(item);
      //     });
      //   }
      // });

      if (route.meta.userContext) {
        refreshParams.clearState =
          refreshParams.clearState || (meta.userContext.clearState ?? false);

        refreshParams.stateUpdate = {
          ...refreshParams.stateUpdate,
          ...meta.userContext.stateUpdate,
        };

        meta.userContext.refresh?.forEach((item) => {
          refreshParams.refresh.add(item);
        });
      }

      // Execute refresh based on route.meta.userContext data.
      await this.refresh(refreshParams);
    } catch (err) {
      console.error(err);
      // What else can we do on error? Redirect to error page?
    } finally {
      // Finally, strip PURL params from URL query, if they were populated.
      if (
        queryBranchCode ||
        queryCustomerId ||
        queryNdc ||
        queryNdcTracking ||
        queryCoupon
      ) {
        const newQuery = { ...route.query };

        delete newQuery.franchise_id;
        delete newQuery.c;
        delete newQuery.ndc;
        delete newQuery.ndc_tracking;
        delete newQuery.coupon;

        this.redirect({
          name: route.name,
          path: route.path,
          params: route.params,
          query: newQuery,
        });
      }
    }
  }

  async refresh({ clearState, stateUpdate, refresh }) {
    try {
      // this.store.commit("SET_SHOW_LOADER", true); // Disable loader for now.

      const startTime = Date.now();

      this.app.$utils.devLog(`Beginning UserContext.refresh(), with options:`);
      this.app.$utils.devLog({
        clearState: clearState,
        stateUpdate: stateUpdate,
        refresh: refresh,
      });

      if (clearState) {
        this.store.commit("CLEAR_STATE");
      }

      if (stateUpdate) {
        if (stateUpdate.branchCode) {
          this.store.commit("SET_BRANCHCODE", stateUpdate.branchCode);
        }

        if (stateUpdate.custNo) {
          this.store.commit("SET_CUSTOMER_NUMBER", stateUpdate.custNo);
        }

        if (stateUpdate.ndc) {
          this.store.commit("SET_NDC_INFO", stateUpdate.ndc);
        }

        if (stateUpdate.addCouponCode) {
          this.store.commit("SET_ADD_COUPON", stateUpdate.addCouponCode);
        }
      }

      // Execute full refresh if specified.
      if (
        refresh.has("ALL") ||
        !this.store.state.persistedState.metadata?.userContext?.lastRefresh
      ) {
        refresh = this.getRefreshAllSet();
      } else {
        // Else, get expired cache refreshes.
        const userContextLastRefresh = this.store.state.persistedState.metadata.userContext.lastRefresh;

        if (
          this.getRefreshDate(userContextLastRefresh.branchInfo, 6 * 60) <=
          startTime
        ) {
          refresh.add("BRANCH_INFO");
        }
        if (
          this.getRefreshDate(userContextLastRefresh.franchiseInfo, 6 * 60) <=
          startTime
        ) {
          refresh.add("FRANCHISE_INFO");
        }
        if (
          this.getRefreshDate(userContextLastRefresh.paymentInfo, 1 * 60) <=
          startTime
        ) {
          refresh.add("PAYMENT_INFO");
        }
        if (
          this.getRefreshDate(userContextLastRefresh.scopeInfo, 1 * 60) <=
          startTime
        ) {
          refresh.add("SCOPE_INFO");
        }
        if (
          this.getRefreshDate(userContextLastRefresh.addressInfo, 6 * 60) <=
          startTime
        ) {
          refresh.add("ADDRESS_INFO");
        }
        if (
          this.getRefreshDate(userContextLastRefresh.cookies, 1 * 60) <=
          startTime
        ) {
          refresh.add("COOKIES");
        }
        if (
          this.getRefreshDate(userContextLastRefresh.orderInfo, 15) <= startTime
        ) {
          refresh.add("ORDER_INFO");
        }
        if (
          this.getRefreshDate(userContextLastRefresh.serviceInfo, 6 * 60) <=
          startTime
        ) {
          refresh.add("SERVICE_INFO");
        }
        if (
          this.getRefreshDate(userContextLastRefresh.packageInfo, 6 * 60) <=
          startTime
        ) {
          refresh.add("PACKAGE_INFO");
        }
        if (
          this.getRefreshDate(userContextLastRefresh.customerInfo, 6 * 60) <=
          startTime
        ) {
          refresh.add("CUSTOMER_INFO");
        }
        if (
          this.getRefreshDate(userContextLastRefresh.conditionalInfo, 1 * 60) <=
          startTime
        ) {
          refresh.add("CONDITIONAL_INFO");
        }
      }

      if (refresh) {
        // Stage 1
        const stage1Tasks = [];
        if (refresh.has("BRANCH_INFO")) {
          stage1Tasks.push(this.refreshBranchInfo());
        }
        await Promise.all(stage1Tasks);

        // Stage 2
        const stage2Tasks = [];
        if (refresh.has("FRANCHISE_INFO")) {
          stage2Tasks.push(this.refreshFranchiseInfo());
        }
        if (refresh.has("SCOPE_INFO")) {
          stage2Tasks.push(this.refreshScopeInfo());
        }
        if (refresh.has("ADDRESS_INFO")) {
          stage2Tasks.push(this.refreshAddressInfo());
        }
        if (refresh.has("PAYMENT_INFO")) {
          stage2Tasks.push(this.refreshPaymentInfo());
        }
        await Promise.all(stage2Tasks);

        // Stage 3
        const stage3Tasks = [];
        if (refresh.has("COOKIES")) {
          stage3Tasks.push(this.refreshCookies());
        }
        if (refresh.has("SERVICE_INFO")) {
          stage3Tasks.push(this.refreshServiceInfo());
        }
        if (refresh.has("CUSTOMER_INFO")) {
          stage3Tasks.push(this.refreshCustomerInfo());
        }
        await Promise.all(stage3Tasks);

        // Stage 4
        const stage4Tasks = [];
        if (refresh.has("ORDER_INFO")) {
          // SSD-2256: refreshOrderInfo() must run after refreshServiceInfo() and before refreshCartInfo().
          stage4Tasks.push(this.refreshOrderInfo());
        }
        await Promise.all(stage4Tasks);

        // Stage 5
        const stage5Tasks = [];
        if (refresh.has("ORDER_INFO") || refresh.has("SERVICE_INFO")) {
          stage5Tasks.push(this.refreshCartInfo());
        }
        if (refresh.has("PACKAGE_INFO") || refresh.has("ORDER_INFO")) {
          stage5Tasks.push(this.refreshPackageInfo());
        }
        if (refresh.has("CONDITIONAL_INFO")) {
          stage5Tasks.push(this.refreshConditionalInfo());
        }
        await Promise.all(stage5Tasks);
      }

      const endTime = Date.now();

      this.app.$utils.devLog(
        `Completed UserContext.refresh() in: ${endTime - startTime} ms.`
      );
    } finally {
      // this.store.commit("SET_SHOW_LOADER", false); // Disable loader for now.
    }
  }

  getRefreshAllSet() {
    return new Set([
      "BRANCH_INFO",
      "FRANCHISE_INFO",
      "SCOPE_INFO",
      "ADDRESS_INFO",
      "PAYMENT_INFO",
      "COOKIES",
      "ORDER_INFO",
      "SERVICE_INFO",
      "PACKAGE_INFO",
      "CUSTOMER_INFO",
      "CONDITIONAL_INFO",
    ]);
  }

  getRefreshDate(startTime, refreshPeriodMinutes) {
    return (startTime ?? 0) + 60 * 1000 * refreshPeriodMinutes;
  }

  async refreshBranchInfo() {
    const startTime = Date.now();

    // Check store dependencies
    const branchCode = this.store.state.persistedState.branchCode;
    if (branchCode) {
      const branchData = await this.app.$sg.getOctoberData(
        `branch/${branchCode}/branchinfo`
      );

      // Branch info object on success, or an empty array on failure.
      if (branchData && !Array.isArray(branchData)) {
        this.store.commit("SET_BRANCH_INFO", branchData);
        this.store.commit("SET_SQLDBID", branchData.SQLDBID);
        const branchPhone = branchData.BranchInfo.InfoData.find(
          data => data.FieldName === "Business Main Phone"
        );
        if (branchPhone) {
          this.app.branchPhone = branchPhone.FieldValue; // What is this referencing?
          this.store.commit("SET_BRANCH_PHONE", branchPhone);
        }
        const branchMarchExPhone = branchData.BranchInfo.InfoData.find(
          data => data.FieldName === "MarchEX Digital Phone"
        );
        if (branchMarchExPhone) {
          this.app.branchMarchExPhone = branchMarchExPhone.FieldValue; // What is this referencing?
          this.store.commit("SET_BRANCH_MARCHEXPHONE", branchMarchExPhone);
        }
      }
    }

    const endTime = Date.now();

    this.store.dispatch("updateMetadataUserContextLastRefresh", {
      branchInfo: endTime,
    });

    this.app.$utils.devLog(
      `Completed branch info refresh in: ${endTime - startTime} ms.`
    );
  }

  async refreshFranchiseInfo() {
    const startTime = Date.now();

    // Check store dependencies
    const sqldbid = this.store.state.persistedState.sqldbid;
    if (sqldbid) {
      const franchiseResponse = await this.app.$sg.getOctoberData(
        `franchise/${sqldbid}/sqldbid`
      );
      // Franchise response is an array on success
      if (franchiseResponse?.length > 0) {
        //set default location
        let franchiseData = franchiseResponse[0];
        //check for multiple, set location to parent location
        if(franchiseResponse.length > 1){
          let filteredFranchise = franchiseResponse.filter(franchise => franchise.parent_id === 0);
          if( filteredFranchise ) {
            franchiseData = filteredFranchise[0];
          }
        }

        this.store.commit("SET_FRANCHISE_DATA", franchiseData);
        this.app.branchLocation = franchiseData.city_state_override; // What is this referencing?
        this.store.commit(
          "SET_BRANCH_LOCATION",
          franchiseData.city_state_override
        );
        this.store.commit("SET_FRANCHISE_SLUG", franchiseData.slug);
      }
    }

    const endTime = Date.now();

    this.store.dispatch("updateMetadataUserContextLastRefresh", {
      franchiseInfo: endTime,
    });

    this.app.$utils.devLog(
      `Completed franchise data refresh in: ${endTime - startTime} ms.`
    );
  }

  async refreshScopeInfo() {
    const startTime = Date.now();

    // Check store dependencies
    const sqldbid = this.store.state.persistedState.sqldbid;
    const custNo = this.store.state.persistedState.cust_No;
    if (sqldbid && custNo) {
      // Retrieve existing scope, or create a new one
      let scopeInfo = await this.app.$scope.getScopeByBranchCustomer(
        sqldbid,
        custNo
      );

      if (!scopeInfo?.id) {
        scopeInfo = await this.app.$scope.createScope({
          sqldbid: sqldbid,
          custNo: custNo,
        });
      }

      this.store.commit("SET_SCOPE", scopeInfo); // This scopeInfo includes "franchiseOnlyScope" (which isn't needed) and may not have the "ndc" value set.

      // Set NDC code
      const existingNdcInfo = this.store.state.persistedState.ndcInfo;
      let ndcInfo;

      if (existingNdcInfo?.ndc) {
        if (existingNdcInfo.tracking) {
          this.app.$ndc.addTouchPoint(
            existingNdcInfo.ndc,
            existingNdcInfo.tracking?.toString().toLowerCase() === "qr"
          );
        }

        ndcInfo = await this.app.$sg.getApiData(
          `Offers/NDC/${existingNdcInfo.ndc}/Offer`
        );

        if (ndcInfo?.error) {
          ndcInfo = undefined;
        }
      }

      if (
        !ndcInfo ||
        ndcInfo.isExpired ||
        ndcInfo.sqldbid != sqldbid ||
        ndcInfo.custNo != custNo
      ) {
        ndcInfo = await this.app.$sg.getApiData(
          `Offers/Customer/${custNo}/Offer/Latest?onlyReturnServiceOffers=true`,
          scopeInfo.id
        );

        if (ndcInfo?.error) {
          ndcInfo = undefined;
        }
      }

      if (ndcInfo && !ndcInfo.isExpired) {
        this.store.commit("SET_NDC_INFO", ndcInfo);

        scopeInfo = await this.app.$scope.updateScope(scopeInfo.id, {
          sqldbid: sqldbid,
          custNo: custNo,
          ndc: ndcInfo.ndc,
          orderId: scopeInfo.orderId,
        });
      } else {
        this.store.commit("SET_NDC_INFO", undefined);
      }
      this.store.commit("SET_SCOPE", scopeInfo);
    }

    const endTime = Date.now();

    this.store.dispatch("updateMetadataUserContextLastRefresh", {
      scopeInfo: endTime,
    });

    this.app.$utils.devLog(
      `Completed scope info refresh in: ${endTime - startTime} ms.`
    );
  }

  async refreshAddressInfo() {
    const startTime = Date.now();

    // Check store dependencies
    const custNo = this.store.state.persistedState.cust_No;
    const sqldbid = this.store.state.persistedState.sqldbid;
    let zipCode = this.store.state.persistedState.addressInfo?.zip?.trim().slice(0,5);
    if (!zipCode) {
      zipCode = this.store.state.persistedState.branchInfo?.ZipCodes ? this.store.state.persistedState.branchInfo?.ZipCodes[0]?.ZipCode?.trim().slice(0,5) : undefined;
    }
    if (custNo && zipCode && sqldbid) {
      const addressRequest = {
        custNo: custNo,
        zip: zipCode,
      };
      const addressResponse = await this.app.$sg.postApiData(
        "Addresses/SG/Search?useFranchizeZipAssignments=true",
        addressRequest
      );

      const addressInfo = addressResponse.find((addressInfo) => addressInfo.cust_No == custNo && addressInfo.sqldbid == sqldbid);
      if (addressInfo) {
        this.store.commit("SET_ADDRESS_INFO", addressInfo);
        this.store.commit(
          "SET_USER_ADDRESS",
          `${addressInfo.address1}, ${addressInfo.city}, ${
            addressInfo.state
          }, ${addressInfo.zip?.trim().slice(0, 5)}`
        );
        this.store.commit("SET_ZIP", addressInfo.zip?.trim().slice(0, 5));
      }
    }

    const endTime = Date.now();

    this.store.dispatch("updateMetadataUserContextLastRefresh", {
      addressInfo: endTime,
    });

    this.app.$utils.devLog(
      `Completed address info refresh in: ${endTime - startTime} ms.`
    );
  }

  async refreshPaymentInfo() {
    const startTime = Date.now();

    // Update Payment Service Status, no dependencies
    const paymentServiceStatus = (await this.app.$sg.getOctoberData("paymentservice/status")) ?? {};
    this.store.dispatch("updatePaymentInfoPaymentServiceStatus", paymentServiceStatus ?? {});

    // Check store dependencies
    const sqldbid = this.store.state.persistedState?.sqldbid;
    const branchInfoSqldbid = this.store.state.persistedState?.branchInfo?.SQLDBID;
    
    const activeSqldbid = sqldbid || branchInfoSqldbid;
        
    if (activeSqldbid) {
      const franchiseDiscounts = await this.app.$discounts.getDiscountsByCompany(activeSqldbid);

      // Add Existing Customer Pre-Pay Discount, if found.
      const existingCustomerPrepPayDiscount = this.app.$discounts.getExistingCustomerPrePayDiscount();
      if (typeof existingCustomerPrepPayDiscount?.PrePayDiscount === "string") {
        const existingCustomerPrePayDiscountValue = convertPercentageToDecimal(existingCustomerPrepPayDiscount.PrePayDiscount);
        if (_.isNumber(existingCustomerPrePayDiscountValue)) {
          franchiseDiscounts.push({
            name: "ExistingCustomerPrePayDiscount",
            value: existingCustomerPrePayDiscountValue,
          });
        }
      }

      // Add Customer Discount (e.g. Senior Discount), if found.
      const customerDiscountCode = this.store.state.persistedState?.customer?.discountCode;
      if (customerDiscountCode) {
        const customerDiscount = await this.app.$discounts.getDiscountCode(activeSqldbid, customerDiscountCode);
        const customerDiscountAmount = customerDiscount?.discountAmount / 100;
        if (_.isNumber(customerDiscountAmount)) {
          franchiseDiscounts.push({
            name: "CustomerDiscount",
            value: customerDiscountAmount,
            description: customerDiscount.discountDescription,
          });
        }
      }

      this.store.dispatch("updatePaymentInfoFranchiseDiscounts", franchiseDiscounts ?? []);

      // Update current service year and installment billing months remaining
      const [serviceYear, remainingMonths] = await Promise.all([
        this.app.$companies.getServiceYear(activeSqldbid),
        this.app.$companies.getRemainingMonths(activeSqldbid),
      ]);

      this.store.dispatch("updatePaymentInfoServiceYear", serviceYear);
      this.store.dispatch("updatePaymentInfoRemainingMonths", remainingMonths);
    }

    const endTime = Date.now();

    this.store.dispatch("updateMetadataUserContextLastRefresh", {
      paymentInfo: endTime,
    });

    this.app.$utils.devLog(
      `Completed payment info refresh in: ${endTime - startTime} ms.`
    );
  }

  async refreshCookies() {
    const startTime = Date.now();

    // Check store dependencies
    const franchiseSlug = this.store.state.persistedState.franchise_slug;
    const sqldbid = this.store.state.persistedState.sqldbid;
    const branchCode = this.store.state.persistedState.branchCode;
    if (franchiseSlug && sqldbid && branchCode) {
      // Save cookies about the branch to allow parallel queries later
      const cookieData = this.app.$sg.getUserFranchiseDataCookie(
        this.app.$cookiz,
        franchiseSlug
      );
      cookieData.franchiseId = sqldbid;
      cookieData.branchCode = branchCode;
      this.app.$sg.saveUserFranchiseDataCookie(this.app.$cookiz, cookieData);

      // Set cookie to let it know not to override
      this.app.$cookiz.set("custom_address", true);
    }

    const endTime = Date.now();

    this.store.dispatch("updateMetadataUserContextLastRefresh", {
      cookies: endTime,
    });

    this.app.$utils.devLog(
      `Completed cookies refresh in: ${endTime - startTime} ms.`
    );
  }

  // Service and order info are confounded together
  async refreshOrderInfo() {
    const startTime = Date.now();

    // Check store dependencies
    const scope = this.store.state.persistedState.scope;
    if (scope) {
      let orderInfo = null;

      if (scope.orderId) {
        orderInfo = await this.app.$orders.getOrderById(
          scope.orderId,
          scope.id
        );
      }

      // Clear invalid order
      if (
        orderInfo?.orderType !== "NORMAL" ||
        orderInfo?.orderStatus !== "PENDING"
      ) {
        orderInfo = null;
      }

      // Create new order, if no (valid) order exists and we have a scope and customer number
      if (!orderInfo) {
        orderInfo = await this.app.$orders.createOrder(
          { custNo: scope.cust_No },
          scope.id
        );
      }

      // Add a coupon, if it doesn't already exist on the order
      const addCouponCode = this.store.state.addCouponCode;
      if (addCouponCode) {
        const orderDiscounts = orderInfo.orderItems.flatMap((orderItem) => orderItem.orderDiscounts);
        const hasCoupon = orderDiscounts.some(
          orderDiscount => orderDiscount.couponCode === addCouponCode
        );
        if (!hasCoupon) {
          orderInfo = await this.app.$orders.addCoupon(
            orderInfo.id,
            addCouponCode,
            scope.id,
            this.store
          );
        }
      }

      this.store.commit("SET_ORDER_INFORMATION", orderInfo);
      this.store.commit("SET_SCOPE_ORDERID", orderInfo?.id);
    }
    const endTime = Date.now();

    this.store.dispatch("updateMetadataUserContextLastRefresh", {
      orderInfo: endTime,
    });

    this.app.$utils.devLog(
      `Completed order info refresh in: ${endTime - startTime} ms.`
    );
  }

  async refreshServiceInfo() {
    const startTime = Date.now();

    // Always execute service refresh, regardless of customer scope id existence.
    await this.app.$services.loadServiceData();

    const endTime = Date.now();

    this.store.dispatch("updateMetadataUserContextLastRefresh", {
      serviceInfo: endTime,
    });

    this.app.$utils.devLog(
      `Completed service info refresh in: ${endTime - startTime} ms.`
    );
  }

  async refreshPackageInfo() {
    const startTime = Date.now();

    // Dependencies
    const scope = this.store.state.persistedState.scope;
    const orderId = this.store.state.persistedState.order?.id;

    // Run independent fetches in parallel
    const tasks = [];

    // Premium Package Info
    if (scope?.cust_No && scope?.sqldbid && scope?.id) {
      tasks.push(
        (async () => {
          const premiumPackageInfo = await this.app.$offers.getPremiumPackageInfo(this.store.state.persistedState);
          this.store.dispatch("updatePremiumPackageInfo", premiumPackageInfo);
        })()
      );
    }

    // Order Premium Package Info
    if (orderId) {
      tasks.push(
        (async () => {
          const isOrderPremiumPackage = await this.app.$orders.IsPremiumPackageAvailable(orderId);
          this.store.dispatch("updateIsOrderPremiumPackage", isOrderPremiumPackage);

          // Update selected package id, if order is premium package
          if (isOrderPremiumPackage && scope?.id) {
            const packageDetails = await this.app.$orders.getPackageDetails(orderId, scope.id);
            this.store.dispatch("updatePackageId", packageDetails?.packageId);
          }
        })()
      );
    }

    // Wait for all tasks to complete
    await Promise.all(tasks);

    const endTime = Date.now();

    this.store.dispatch("updateMetadataUserContextLastRefresh", {
      packageInfo: endTime,
    });

    this.app.$utils.devLog(
      `Completed package info refresh in: ${endTime - startTime} ms.`
    );
  }

  async refreshCustomerInfo() {
    const startTime = Date.now();

    // Check store dependencies
    const loggedIn = this.auth.status.value === "authenticated";
    const custNo = this.store.state.persistedState.cust_No;
    const sqldbid = this.store.state.persistedState.sqldbid;
    const lastName = this.store.state.persistedState.addressInfo?.lastName;
    const zipCode = this.store.state.persistedState.addressInfo?.zip
      ?.trim()
      .slice(0, 5);
    let customer = null;

    if (loggedIn && custNo && sqldbid) {
      customer = await this.app.$customers.getCustomerById(custNo, sqldbid);
    } else if (custNo && lastName && zipCode) {
      customer = await this.app.$customers.getCustomerByInfo(
        custNo,
        lastName,
        zipCode
      );
    }

    if (customer && !customer.error) {
      this.store.commit("SET_CUSTOMER", customer);
    }

    const endTime = Date.now();

    this.store.dispatch("updateMetadataUserContextLastRefresh", {
      customerInfo: endTime,
    });

    this.app.$utils.devLog(
      `Completed customer info refresh in: ${endTime - startTime} ms.`
    );
  }

  async refreshCartInfo() {
    const startTime = Date.now();

    // Check store dependencies
    const orderInfo = this.store.state.persistedState.order;
    const serviceData = this.store.state.persistedState.serviceData;
    if (orderInfo && serviceData) {
      this.app.$services.applyOrders();
    }

    const endTime = Date.now();

    this.store.dispatch("updateMetadataUserContextLastRefresh", {
      cartInfo: endTime,
    });

    this.app.$utils.devLog(
      `Completed cart info refresh in: ${endTime - startTime} ms.`
    );
  }

  async refreshConditionalInfo() {
    const startTime = Date.now();

    // Set billing email
    // Prefer customer schema over addressInfo schema.
    const customerData = this.store.state.persistedState.customer;
    const addressInfo = this.store.state.persistedState.addressInfo;
    const billingEmail =
      customerData?.preferredEmail ?? customerData?.email ?? addressInfo?.email;
    this.store.commit("SET_BILLING_EMAIL", billingEmail);

    const endTime = Date.now();

    this.store.dispatch("updateMetadataUserContextLastRefresh", {
      conditionalInfo: endTime,
    });

    this.app.$utils.devLog(
      `Completed conditional data refresh in: ${endTime - startTime} ms.`
    );
  }
}
